import React, { useState } from "react";
import {
  FaChevronDown,
  FaChevronRight,
  FaSave,
  FaCheckSquare,
  FaRegSquare,
  FaMinusSquare,
} from "react-icons/fa";

// Constants defining exactly what permissions exist per feature
const PERM_TYPES = {
  CREATE: "create",
  READ: "read",
  UPDATE: "update",
  DELETE: "delete",
};

// Data structure mirroring the application sidebar and features
const APP_MODULES = [
  {
    id: "general",
    title: "General",
    features: [
      { id: "overview", name: "Overview Dashboard", perms: [PERM_TYPES.READ] },
      {
        id: "onboarding",
        name: "On-boarding Form Response",
        perms: [PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
    ],
  },
  {
    id: "users",
    title: "Users Management",
    features: [
      {
        id: "all_users",
        name: "All Users",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "emergency_contacts",
        name: "Emergency Contacts",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
    ],
  },
  {
    id: "attendance_management",
    title: "Attendance Management",
    features: [
      // Leave Management sub-tab
      {
        id: "leave_requests_apply",
        name: "Apply Leave",
        perms: [PERM_TYPES.CREATE, PERM_TYPES.READ],
      },
      {
        id: "od_requests",
        name: "OD (Official Duty)",
        perms: [PERM_TYPES.CREATE, PERM_TYPES.READ],
      },
      {
        id: "coplus_requests",
        name: "CO+ (Compensatory Off)",
        perms: [PERM_TYPES.CREATE, PERM_TYPES.READ],
      },
      {
        id: "check_leave_balances_team",
        name: "Check Team Leave Balances",
        perms: [PERM_TYPES.READ],
      },
      {
        id: "my_leave_balance",
        name: "My Leave Balance",
        perms: [PERM_TYPES.READ],
      },
      { id: "my_calendar", name: "My Calendar", perms: [PERM_TYPES.READ] },
      { id: "my_approvers", name: "My Approvers", perms: [PERM_TYPES.READ] },

      // Attendance sub-tab
      {
        id: "daily_attendance",
        name: "Daily Attendance",
        perms: [PERM_TYPES.READ],
      },
      {
        id: "check_attendance_team",
        name: "Check Team Attendance",
        perms: [PERM_TYPES.READ],
      },
      {
        id: "upload_attendance",
        name: "Upload Attendance (Bulk)",
        perms: [PERM_TYPES.CREATE, PERM_TYPES.READ],
      },
      {
        id: "clock_in_out",
        name: "Clock-In/Out",
        perms: [PERM_TYPES.CREATE, PERM_TYPES.READ],
      },

      // Requests sub-tab
      {
        id: "swipe_requests",
        name: "Swipe Requests",
        perms: [PERM_TYPES.CREATE, PERM_TYPES.READ],
      },
      {
        id: "sto_requests",
        name: "Short Time Off (STO)",
        perms: [PERM_TYPES.CREATE, PERM_TYPES.READ],
      },
      {
        id: "shift_change_requests",
        name: "Shift Change",
        perms: [PERM_TYPES.CREATE, PERM_TYPES.READ],
      },

      // Approvals sub-tab
      {
        id: "approve_checkin_out",
        name: "Approve Check-In/Out",
        perms: [PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
      {
        id: "approve_swipe",
        name: "Approve Swipe",
        perms: [PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
      {
        id: "approve_coplus",
        name: "Approve CO+",
        perms: [PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
      {
        id: "approve_od",
        name: "Approve OD",
        perms: [PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
      {
        id: "approve_sto",
        name: "Approve STO",
        perms: [PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
      {
        id: "approve_shift_change",
        name: "Approve Shift Change",
        perms: [PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
      {
        id: "approve_leave",
        name: "Approve Leave Requests",
        perms: [PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
    ],
  },
  {
    id: "payroll",
    title: "Payroll Management",
    features: [
      {
        id: "payroll_config",
        name: "Payroll Configuration",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "salary_config",
        name: "Salary Configuration",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "payroll_settings",
        name: "Payroll Settings",
        perms: [PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
      {
        id: "component_types",
        name: "Component Types",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "tax_config",
        name: "Tax Configurations",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },

      {
        id: "employee_mgmt",
        name: "Employee Management",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "salary_structures",
        name: "Salary Structures",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "employee_salary",
        name: "Employee Salary",
        perms: [PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
      {
        id: "salary_history",
        name: "Salary History",
        perms: [PERM_TYPES.READ],
      },
      {
        id: "salary_fitment",
        name: "Salary Fitment",
        perms: [PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
      {
        id: "bulk_update",
        name: "Bulk Update",
        perms: [PERM_TYPES.CREATE, PERM_TYPES.READ],
      },
      {
        id: "import_export",
        name: "Import/Export Data",
        perms: [PERM_TYPES.CREATE, PERM_TYPES.READ],
      },

      { id: "payroll_list", name: "Payroll List", perms: [PERM_TYPES.READ] },
      {
        id: "generate_payroll",
        name: "Generate Payroll",
        perms: [PERM_TYPES.CREATE, PERM_TYPES.READ],
      },
      {
        id: "pending_approvals",
        name: "Pending Approvals",
        perms: [PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
      {
        id: "approval_history",
        name: "Approval History",
        perms: [PERM_TYPES.READ],
      },
      {
        id: "reports_mgmt",
        name: "Reports Management",
        perms: [PERM_TYPES.READ],
      },
    ],
  },
  {
    id: "recruitment",
    title: "Recruitment",
    features: [
      {
        id: "recruitment_dashboard",
        name: "Dashboard",
        perms: [PERM_TYPES.READ],
      },
      {
        id: "hiring_request",
        name: "Hiring Request",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "hod_requests",
        name: "HOD Requests",
        perms: [PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
      {
        id: "application_responses",
        name: "Application Responses",
        perms: [PERM_TYPES.READ],
      },
      {
        id: "campaigns",
        name: "Campaigns",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "all_candidates",
        name: "All Candidates",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "interviews",
        name: "Interviews",
        perms: [PERM_TYPES.CREATE, PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
      {
        id: "resignations",
        name: "Resignations",
        perms: [PERM_TYPES.CREATE, PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
    ],
  },
  {
    id: "file_management",
    title: "File Management",
    features: [
      {
        id: "upload_resumes",
        name: "Upload Resumes",
        perms: [PERM_TYPES.CREATE, PERM_TYPES.READ],
      },
      {
        id: "manage_files",
        name: "Manage Files",
        perms: [PERM_TYPES.CREATE, PERM_TYPES.READ, PERM_TYPES.DELETE],
      },
    ],
  },
  {
    id: "settings",
    title: "Settings",
    features: [
      {
        id: "role_settings",
        name: "Roles Configuration",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "permission_settings",
        name: "Permissions Assignment",
        perms: [PERM_TYPES.READ, PERM_TYPES.UPDATE],
      },
    ],
  },
  {
    id: "master",
    title: "Master Data",
    features: [
      {
        id: "master_employment_type",
        name: "Employment Type",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_departments",
        name: "Departments",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_designations",
        name: "Designations",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_branch",
        name: "Branch/Workplace",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_state_code",
        name: "State Code",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_employee_grade",
        name: "Employee Grade",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_categories",
        name: "Categories",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_salary_grade",
        name: "Salary Grade",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_leave_type",
        name: "Leave Type",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_shifts",
        name: "Shifts",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_holidays",
        name: "Holidays",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_leave_balance",
        name: "Leave Balance Master",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_forms",
        name: "Forms Master",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_dept_hods",
        name: "Department HODs",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_approver_mod",
        name: "Approver Modules",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_approval_lvl",
        name: "Approval Level",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_emp_approver",
        name: "Employee Approver",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_leave_policy",
        name: "Leave Policy",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
      {
        id: "master_salary_comp",
        name: "Salary Component",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
    ],
  },
  {
    id: "policy",
    title: "Company Policy",
    features: [
      {
        id: "policy_mgmt",
        name: "Policy Management",
        perms: [
          PERM_TYPES.CREATE,
          PERM_TYPES.READ,
          PERM_TYPES.UPDATE,
          PERM_TYPES.DELETE,
        ],
      },
    ],
  },
];

const RolePermissionsForm = ({
  initialName = "",
  initialPermissions = {},
  onSave,
  onCancel,
  type = "Role", // Can be "Role" or "Permission"
}) => {
  const [name, setName] = useState(initialName);

  // flattened state of permissions: { "featureId_permType": boolean }
  // e.g. { "all_users_read": true, "all_users_create": false }
  const [permissions, setPermissions] = useState(initialPermissions);

  const [expandedModules, setExpandedModules] = useState(
    APP_MODULES.reduce((acc, mod) => ({ ...acc, [mod.id]: true }), {}),
  );

  // Track last seen props to avoid fighting user input
  const lastInitialName = React.useRef(initialName);
  const lastInitialPermissions = React.useRef(
    JSON.stringify(initialPermissions),
  );

  // Sync name only if the PROP changes from its PREVIOUS value
  React.useEffect(() => {
    if (initialName && initialName !== lastInitialName.current) {
      setName(initialName);
      lastInitialName.current = initialName;
    }
  }, [initialName]);

  // Sync permissions only if the PROP changes from its PREVIOUS value
  React.useEffect(() => {
    const serialized = JSON.stringify(initialPermissions);
    if (
      Object.keys(initialPermissions).length > 0 &&
      serialized !== lastInitialPermissions.current
    ) {
      setPermissions(initialPermissions);
      lastInitialPermissions.current = serialized;
    }
  }, [initialPermissions]);

  const toggleModule = (modId) => {
    setExpandedModules((prev) => ({ ...prev, [modId]: !prev[modId] }));
  };

  const handlePermChange = (featureId, permType, isChecked) => {
    setPermissions((prev) => ({
      ...prev,
      [`${featureId}_${permType}`]: isChecked,
    }));
  };

  const isPermChecked = (featureId, permType) => {
    return !!permissions[`${featureId}_${permType}`];
  };

  // Check state of a feature (row level)
  const getFeatureSelectionState = (feature) => {
    let checkedCount = 0;
    feature.perms.forEach((p) => {
      if (isPermChecked(feature.id, p)) checkedCount++;
    });

    if (checkedCount === 0) return "none";
    if (checkedCount === feature.perms.length) return "all";
    return "partial";
  };

  const handleFeatureToggleAll = (feature) => {
    const currentState = getFeatureSelectionState(feature);
    const newValue = currentState !== "all"; // if partial or none, turn all ON. if all, turn all OFF.

    const newPerms = { ...permissions };
    feature.perms.forEach((p) => {
      newPerms[`${feature.id}_${p}`] = newValue;
    });
    setPermissions(newPerms);
  };

  // Check state of a module (module level)
  const getModuleSelectionState = (moduleObj) => {
    let totalPerms = 0;
    let checkedCount = 0;

    moduleObj.features.forEach((feat) => {
      feat.perms.forEach((p) => {
        totalPerms++;
        if (isPermChecked(feat.id, p)) checkedCount++;
      });
    });

    if (checkedCount === 0) return "none";
    if (checkedCount === totalPerms) return "all";
    return "partial";
  };

  const handleModuleToggleAll = (moduleObj) => {
    const currentState = getModuleSelectionState(moduleObj);
    const newValue = currentState !== "all";

    const newPerms = { ...permissions };
    moduleObj.features.forEach((feat) => {
      feat.perms.forEach((p) => {
        newPerms[`${feat.id}_${p}`] = newValue;
      });
    });
    setPermissions(newPerms);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      alert(`${type} Name is required`);
      return;
    }

    // Transform flat state into whatever nested object your API needs onSave.
    onSave({ name: name, permissions });
  };

  // Helper for rendering checkboxes
  const renderCheckboxOrDash = (feature, permType) => {
    if (!feature.perms.includes(permType)) {
      return (
        <span className="text-gray-300 dark:text-gray-600 block text-center">
          -
        </span>
      );
    }
    return (
      <div className="flex justify-center">
        <input
          type="checkbox"
          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
          checked={isPermChecked(feature.id, permType)}
          onChange={(e) =>
            handlePermChange(feature.id, permType, e.target.checked)
          }
        />
      </div>
    );
  };

  const getIconForState = (state) => {
    if (state === "all")
      return <FaCheckSquare className="text-blue-600 w-5 h-5 mx-auto" />;
    if (state === "partial")
      return (
        <FaMinusSquare className="text-blue-600 w-5 h-5 opacity-70 mx-auto" />
      );
    return <FaRegSquare className="text-gray-400 w-5 h-5 mx-auto" />;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden w-full">
      <div className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 p-5 sm:p-6 pb-6">
        <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-6">
          Configure {type}
        </h2>

        <div className="w-full max-w-lg">
          <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
            {type} Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            required
            placeholder={`e.g. ${type === "Role" ? "Senior HR Manager" : "View Attendance"}`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-4 py-2.5 dark:bg-black dark:text-white dark:border-gray-700 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="p-5 sm:p-6 overflow-x-auto">
        <div className="min-w-[800px]">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-2 pb-3 border-b border-gray-300 dark:border-gray-600 font-semibold text-gray-600 dark:text-gray-400 text-sm uppercase tracking-wider mb-2">
            <div className="col-span-4 pl-2">Module / Feature</div>
            <div
              className="col-span-2 text-center border-l dark:border-gray-600 cursor-help"
              title="Select all CRUD permissions for this row"
            >
              All
            </div>
            <div className="col-span-1 text-center border-l dark:border-gray-600">
              Read
            </div>
            <div className="col-span-2 text-center border-l dark:border-gray-600">
              Create
            </div>
            <div className="col-span-1 text-center border-l dark:border-gray-600">
              Update
            </div>
            <div className="col-span-2 text-center border-l dark:border-gray-600">
              Delete
            </div>
          </div>

          {/* Table Body */}
          <div className="space-y-4">
            {APP_MODULES.map((mod) => (
              <div
                key={mod.id}
                className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800"
              >
                {/* Module Header */}
                <div className="grid grid-cols-12 gap-2 bg-gray-100 dark:bg-gray-900/50 p-3 items-center hover:bg-gray-200 dark:hover:bg-gray-700/50 transition-colors">
                  <div
                    className="col-span-4 flex items-center gap-2 cursor-pointer font-bold text-gray-800 dark:text-gray-200"
                    onClick={() => toggleModule(mod.id)}
                  >
                    {expandedModules[mod.id] ? (
                      <FaChevronDown size={14} />
                    ) : (
                      <FaChevronRight size={14} />
                    )}
                    {mod.title}
                  </div>
                  <div className="col-span-2 flex items-center justify-center border-l dark:border-gray-700 border-gray-300 pl-2">
                    <button
                      type="button"
                      onClick={() => handleModuleToggleAll(mod)}
                      className="focus:outline-none"
                    >
                      {getIconForState(getModuleSelectionState(mod))}
                    </button>
                  </div>
                  <div className="col-span-6"></div>{" "}
                  {/* Empty space for parent header row */}
                </div>

                {/* Features (Children) */}
                {expandedModules[mod.id] && (
                  <div className="divide-y divide-gray-100 dark:divide-gray-800">
                    {mod.features.map((feat) => (
                      <div
                        key={feat.id}
                        className="grid grid-cols-12 gap-2 p-3 items-center hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors"
                      >
                        <div className="col-span-4 pl-8 text-sm font-medium text-gray-700 dark:text-gray-300">
                          {feat.name}
                        </div>
                        <div className="col-span-2 border-l border-gray-200 dark:border-gray-700">
                          <button
                            type="button"
                            onClick={() => handleFeatureToggleAll(feat)}
                            className="focus:outline-none w-full flex justify-center"
                          >
                            {getIconForState(getFeatureSelectionState(feat))}
                          </button>
                        </div>
                        <div className="col-span-1 border-l border-gray-200 dark:border-gray-700">
                          {renderCheckboxOrDash(feat, PERM_TYPES.READ)}
                        </div>
                        <div className="col-span-2 border-l border-gray-200 dark:border-gray-700">
                          {renderCheckboxOrDash(feat, PERM_TYPES.CREATE)}
                        </div>
                        <div className="col-span-1 border-l border-gray-200 dark:border-gray-700">
                          {renderCheckboxOrDash(feat, PERM_TYPES.UPDATE)}
                        </div>
                        <div className="col-span-2 border-l border-gray-200 dark:border-gray-700 pl-4 pr-4">
                          {renderCheckboxOrDash(feat, PERM_TYPES.DELETE)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 p-5 flex items-center justify-end gap-3 rounded-b-xl">
        <button
          onClick={onCancel}
          className="px-5 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 font-medium transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={!name.trim()}
          className="px-6 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 shadow-sm flex items-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <FaSave /> Save {type}
        </button>
      </div>
    </div>
  );
};

export default RolePermissionsForm;
